/* La Sura - the toppings for the cake preview drawing. Loaded after
   lasura-cake-draw-light.js, before lasura-cake-draw-toppings-more.js and
   lasura-cake-draw.js.

   Each topping is picked by its Preview style (Sprinkles, Berries,
   Strawberries, Macarons, Meringue kisses here; Rosettes, Flowers, Cherries,
   Chocolate shards, Gold leaf and Candles in -toppings-more.js) and can carry a
   Preview colour, so gold sprinkles or blush meringues draw in that colour.
   A topping arrives as "style" or "style|#hex". Each style lists its pieces
   as [y, markup, delay, x], x and y the point it stands on; every piece becomes its own group, so the preview
   can land them one after another, and all of them, whatever the style, are
   drawn back to front, so as the cake turns what comes round to the front
   is in front. Each is shaded as a solid: a soft shadow, light from the top left. */
(() => {
  const { P, f1, hex, rng, hash, shade } = window.LasuraCakeParts;
  const L = window.LasuraCakeLight;
  const split = (key) => {
    const [st, c] = String(key).split('|');
    return { st, c: hex(c, '') };
  };
  const has = (s, st) => s.toppings.some((k) => split(k).st === st);

  /* Shared by both topping files. */
  const shadow = (x, y, w) => `<ellipse cx="${f1(x)}" cy="${f1(y + 0.8)}" rx="${f1(w)}" ry="${f1(w * 0.34)}" fill="${P.shadow}" opacity=".14"/>`;
  const ball = (x, y, r, fill) => `<circle cx="${f1(x)}" cy="${f1(y)}" r="${f1(r)}" fill="${fill}"/><circle cx="${f1(x)}" cy="${f1(y)}" r="${f1(r)}" fill="${L.url('ball')}"/>`;
  const kinds = {};

  kinds.sprinkles = ({ r, col, sc, at }) => {
    const cols = col ? [col, shade(col, 22), shade(col, -18), shade(col, 40)] : P.sprinkles;
    const t = [];
    /* Lying on the top, so turned in its plane and seen at an angle. */
    for (let i = 0; i < Math.round(52 * sc); i++) {
      const [x, y] = at(r() * Math.PI * 2, Math.sqrt(r()) * 0.84), c = cols[i % cols.length];
      t.push([y, `<g transform="translate(${f1(x)} ${f1(y)}) scale(1 .62) rotate(${Math.round(r() * 180)})"><rect x="-3.1" y="-1" width="6.2" height="2" rx="1" fill="${c}"/><rect x="-2.3" y="-.8" width="4.4" height=".6" rx=".3" fill="${P.white}" opacity=".5"/></g>`, r() * 260, x]);
    }
    return t;
  };

  /* Raspberries (a cluster of drupelets) and blueberries (a dusty bloom and a crown). */
  kinds.berries = ({ r, col, sc, at }) => {
    const n = Math.round(9 * sc) + 2;
    const t = [];
    for (let i = 0; i < n; i++) {
      const [x, y] = at(Math.PI * 1.06 + (i * Math.PI * 0.88) / (n - 1), 0.7 + r() * 0.08);
      if (i % 2) {
        const k = 5.2 * sc, b = P.blueberry;
        t.push([y, shadow(x, y + k * 0.7, k) + ball(x, y, k, b) + `<circle cx="${f1(x)}" cy="${f1(y)}" r="${f1(k)}" fill="${P.white}" opacity=".12"/><circle cx="${f1(x + k * 0.25)}" cy="${f1(y - k * 0.55)}" r="${f1(k * 0.24)}" fill="${shade(b, -45)}"/>`, i * 45, x]);
      } else {
        const k = 7.5 * sc, d = k * 0.4, c = col || P.raspberry;
        let berry = shadow(x, y + k * 0.8, k);
        /* Back drupelets first. */
        [[0, -0.55], [-0.55, -0.25], [0.55, -0.25], [0, 0], [-0.5, 0.35], [0.5, 0.35], [0, 0.55]].forEach(([dx, dy]) => (berry += ball(x + dx * k, y + dy * k, d, c)));
        t.push([y, berry, i * 45, x]);
      }
    }
    return t;
  };

  kinds.strawberries = ({ r, col, sc, ring }) => {
    const fruit = col || P.strawberry, k = sc * 1.3;
    const t = [];
    ring(Math.round(6 * sc) + 2, 0.68, 0.3).forEach(([x, y], i) => {
      const body = `M${f1(x)},${f1(y - 9 * k)}C${f1(x + 7.5 * k)},${f1(y - 9 * k)} ${f1(x + 7 * k)},${f1(y + 2 * k)} ${f1(x)},${f1(y + 8 * k)}C${f1(x - 7 * k)},${f1(y + 2 * k)} ${f1(x - 7.5 * k)},${f1(y - 9 * k)} ${f1(x)},${f1(y - 9 * k)}Z`;
      let seeds = '';
      for (let j = 0; j < 9; j++) {
        const sx = x + (r() - 0.5) * 9 * k, sy = y + (r() - 0.62) * 12 * k;
        seeds += `<ellipse cx="${f1(sx)}" cy="${f1(sy)}" rx=".55" ry=".85" fill="${shade(fruit, -30)}"/><ellipse cx="${f1(sx)}" cy="${f1(sy - 0.2)}" rx=".4" ry=".6" fill="${P.seed}"/>`;
      }
      const leaf = (dx, dy) => `<path d="M${f1(x)},${f1(y - 8.6 * k)}q${f1(dx * 0.6 * k)},${f1(dy * 0.3 * k)} ${f1(dx * k)},${f1(dy * k)}q${f1(-dx * 0.6 * k)},${f1(-dy * 0.1 * k)} ${f1(-dx * k)},${f1(-dy * k)}Z" fill="${P.leaf}" stroke="${shade(P.leaf, -25)}" stroke-width=".4"/>`;
      t.push([y, shadow(x, y + 7 * k, 6 * k) + `<path d="${body}" fill="${fruit}"/><path d="${body}" fill="${L.url('ball')}"/>${seeds}${leaf(-5, -1.6)}${leaf(5, -1.6)}${leaf(-1.8, -3.8)}${leaf(2, -3.6)}<ellipse cx="${f1(x - 2.6 * k)}" cy="${f1(y - 4 * k)}" rx="${f1(1.3 * k)}" ry="${f1(2.6 * k)}" fill="${P.white}" opacity=".45" transform="rotate(20 ${f1(x - 2.6 * k)} ${f1(y - 4 * k)})"/>`, i * 55, x]);
    });
    return t;
  };

  /* Two shells with frilled feet round a band of cream, the top a glossy dome. */
  kinds.macarons = ({ col, sc, W, at }) => {
    const cols = col ? [col, shade(col, 12), shade(col, -8), shade(col, 20), shade(col, 4)] : P.macarons, n = W < 180 ? 3 : 5, k = sc * 1.25;
    const band = (y1, y2, rx, ry) => `M-${rx},${y1}A${rx},${ry} 0 0 0 ${rx},${y1}L${rx},${y2}A${rx},${ry} 0 0 1 -${rx},${y2}Z`;
    const feet = (y, c) => `<path d="M-11.6,${y}A11.6,5.3 0 0 0 11.6,${y}" fill="none" stroke="${shade(c, 14)}" stroke-width="1.7" stroke-dasharray="1.1 .9"/>`;
    const t = [];
    for (let i = 0; i < n; i++) {
      const [x, y] = at(Math.PI * 1.14 + (i * Math.PI * 0.72) / (n - 1), 0.66), c = cols[i];
      t.push([y, `<g transform="translate(${f1(x)} ${f1(y)}) scale(${f1(k)})"><ellipse cy="6" rx="13" ry="3.4" fill="${P.shadow}" opacity=".14"/><path d="${band(0.6, 3.4, 12, 5.5)}" fill="${shade(c, -10)}"/><path d="${band(0.6, 3.4, 12, 5.5)}" fill="${L.url('cyl')}"/>${feet(0.9, c)}<path d="${band(-2.4, 0.4, 10.8, 5)}" fill="${P.cream}"/><path d="${band(-2.4, 0.4, 10.8, 5)}" fill="${L.url('cyl')}" opacity=".6"/>${feet(-2.4, c)}<ellipse cy="-3.6" rx="12" ry="5.5" fill="${c}"/><ellipse cy="-3.6" rx="12" ry="5.5" fill="${L.url('ball')}" opacity=".85"/></g>`, i * 70, x]);
    }
    return t;
  };

  /* Piped meringue: ridged, glossy, its tip lightly toasted. */
  kinds['meringue kisses'] = ({ col, sc, ring }) => {
    const m = col || P.meringue, k = sc * 1.35;
    const t = [];
    ring(Math.round(8 * sc) + 2, 0.72, 0.2).forEach(([x, y], i) => {
      const d = `M${f1(x - 7.5 * k)},${f1(y + 2 * k)}Q${f1(x - 8 * k)},${f1(y - 6 * k)} ${f1(x)},${f1(y - 16 * k)}Q${f1(x + 8 * k)},${f1(y - 6 * k)} ${f1(x + 7.5 * k)},${f1(y + 2 * k)}Q${f1(x)},${f1(y + 5 * k)} ${f1(x - 7.5 * k)},${f1(y + 2 * k)}Z`;
      let ridges = '';
      for (const dx of [-4, 0, 4]) ridges += `<path d="M${f1(x + dx * k)},${f1(y + 2.5 * k)}Q${f1(x + dx * 0.5 * k)},${f1(y - 7 * k)} ${f1(x)},${f1(y - 14 * k)}" fill="none" stroke="${shade(m, -14)}" stroke-width=".8" opacity=".7"/>`;
      t.push([y, shadow(x, y + 3 * k, 7.5 * k) + `<path d="${d}" fill="${m}"/><path d="${d}" fill="${L.url('cyl')}" opacity=".6"/>${ridges}<path d="M${f1(x - 2.2 * k)},${f1(y - 11.5 * k)}Q${f1(x)},${f1(y - 17 * k)} ${f1(x + 2.2 * k)},${f1(y - 11.5 * k)}Z" fill="${shade(m, -16)}" opacity=".45"/><ellipse cx="${f1(x - 2.5 * k)}" cy="${f1(y - 5 * k)}" rx="${f1(1.4 * k)}" ry="${f1(3 * k)}" fill="${P.white}" opacity=".7"/>`, i * 45, x]);
    });
    return t;
  };

  /* Styles that stand up; the rest lie low on the top. Not cherries: their
     stems stand, but squashed from above the fruit went flat, so they keep
     their shape as berries do. */
  const TALL = ['candles', 'chocolate shards', 'meringue kisses', 'strawberries', 'cutout']; /* brand-check allow: topping names, not colours */

  /* Everything that sits on the top surface, back to front. As the camera
     rises to look down (hf, from 1 to 0; tilt, from the usual angle to 1),
     each piece is seen more from above: what stands up shortens, what lies
     low opens out towards its round top, both about the point it stands on. */
  function toppings(s, g, anim) {
    const { W, sc, on, top, frost, hf = 1, tilt = 0.26 } = g;
    const at = (t, rr) => on(t, top, rr), all = [];
    const seen = (st, x, y, m) => {
      if (hf > 0.999 || x == null) return m;
      /* Berries and cherries are balls, round from any side; macarons have a side as well as a top, so open out only a little. */
      const k = Math.round((TALL.includes(st) ? Math.max(0.22, hf) : Math.min(st === 'berries' || st === 'cherries' ? 1 : st === 'macarons' ? 1.3 : 2.2, tilt / 0.26)) * 1000) / 1000;
      return `<g transform="translate(${f1(x)} ${f1(y)}) scale(1 ${k}) translate(${f1(-x)} ${f1(-y)})">${m}</g>`;
    };
    const ring = (n, rr, from = 0) => Array.from({ length: n }, (_, i) => at(from + (i / n) * Math.PI * 2, rr));
    s.toppings.forEach((key) => {
      const { st, c: col } = split(key), kind = kinds[st], fresh = anim.tops && anim.tops.includes(key) ? ' is-new' : '';
      if (kind) kind({ r: rng(hash(key + W)), col, sc, W, at, ring, frost, top }).forEach(([y, m, ms, x]) => all.push([y, `<g class="lasura-cake-preview__pc${fresh}" style="--lasura-d:${Math.round(ms)}ms">${seen(st, x, y, m)}</g>`]));
    });
    /* Cut-outs (lasura-cake-draw-cutout.js) stand up, as candles do. */
    (window.LasuraCakeCutout?.pieces(s, W, at) || []).forEach(([y, m, ms, x, u]) => all.push([y, `<g class="lasura-cake-preview__pc${anim.tops?.includes(u) ? ' is-new' : ''}" style="--lasura-d:${ms}ms">${seen('cutout', x, y, m)}</g>`]));
    return all.length ? `<g class="lasura-cake-preview__tp">${all.sort((a, b) => a[0] - b[0]).map((p) => p[1]).join('')}</g>` : '';
  }

  window.LasuraCakeToppings = { split, has, toppings, kinds, shadow, ball };
})();
